import { useState } from "react";
import { airlineLogo, duffelLogo, gbp } from "@/lib/api";
import { BookingDialog } from "@/components/BookingDialog";
import { QuoteDialog } from "@/components/QuoteDialog";
import { Button } from "@/components/ui/button";
import { Plane, ArrowRight, FileText, ChevronDown, Clock, Info } from "lucide-react";

const Logo = ({ code, name, cls }) => (
  <img src={duffelLogo(code)} alt={name || code} className={cls}
    onError={(e) => { const avs = airlineLogo(code); if (e.target.src !== avs) { e.target.src = avs; } else { e.target.style.display = "none"; } }} />
);

// Summary row for one direction (Skyscanner-style: times, stops, duration)
const DirectionSummary = ({ label, legs, fallback }) => {
  const first = legs[0] || fallback.first;
  const last = legs[legs.length - 1] || fallback.last;
  const stops = Math.max(0, legs.length - 1);
  const via = legs.slice(0, -1).map((l) => l.to_code).filter(Boolean);
  const stopText = stops === 0 ? "Direct" : `${stops} stop${stops > 1 ? "s" : ""}${via.length ? ` · ${via.join(", ")}` : ""}`;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#328CC1] w-14 shrink-0">{label}</span>
      <div className="text-center">
        <p className="font-display text-lg font-bold text-slate-900 leading-none">{first?.dep || fallback.dep}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{first?.from_code || fallback.from}</p>
      </div>
      <div className="flex-1 px-1 flex flex-col items-center">
        <span className="text-[10px] text-slate-400">{fallback.dur}</span>
        <div className="w-full flex items-center gap-1 text-[#328CC1] my-0.5">
          <div className="h-px flex-1 bg-slate-200" /><Plane className="w-3 h-3" /><div className="h-px flex-1 bg-slate-200" />
        </div>
        <span className={`text-[9px] font-semibold ${stops === 0 ? "text-emerald-600" : "text-amber-600"}`}>{stopText}</span>
      </div>
      <div className="text-center">
        <p className="font-display text-lg font-bold text-slate-900 leading-none">{last?.arr || fallback.arr}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{last?.to_code || fallback.to}</p>
      </div>
    </div>
  );
};

// Full per-segment detail (flight number, aircraft, layover)
const SegmentDetail = ({ legs, title }) => (
  <div className="mt-3">
    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</p>
    <div className="space-y-0">
      {legs.map((l, i) => (
        <div key={i}>
          {l.layover && (
            <div className="flex items-center gap-2 py-2 pl-1 text-[11px] text-amber-700">
              <Clock className="w-3.5 h-3.5" /> {l.layover} layover in {l.from} ({l.from_code})
            </div>
          )}
          <div className="flex gap-3 py-2 border-l-2 border-slate-100 pl-3">
            <Logo code={l.airline_code} name={l.airline} cls="h-5 w-auto object-contain mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">{l.dep} {l.from} <span className="text-slate-400">({l.from_code})</span></p>
                <span className="text-[10px] text-slate-400 shrink-0">{l.duration}</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">{l.arr} {l.to} <span className="text-slate-400">({l.to_code})</span></p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {l.airline} · Flight {l.flight_no}{l.aircraft ? ` · ${l.aircraft}` : ""} · {l.cabin || "Economy"}
              </p>
              <p className="text-[11px] text-slate-400">{l.date}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const FlightCard = ({ f, pax }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const legs = Array.isArray(f.legs) ? f.legs : [];
  const outbound = legs.filter((l) => String(l.label || "").toLowerCase().startsWith("outbound"));
  const returnLegs = legs.filter((l) => String(l.label || "").toLowerCase().startsWith("return"));
  const isReturn = f.trip_type === "Return" && (returnLegs.length > 0 || f.ret_depart_time);
  const hasSegments = legs.length > 0;

  const obFallback = { first: null, last: null, dep: f.depart_time, arr: f.arrive_time, from: f.origin_code, to: f.destination_code, dur: f.duration };
  const rtFallback = { first: null, last: null, dep: f.ret_depart_time, arr: f.ret_arrive_time, from: f.destination_code, to: f.origin_code, dur: f.ret_duration };

  return (
    <div data-testid={`flight-card-${f.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-[0_12px_40px_rgb(11,60,93,0.12)] transition-all duration-300">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <img src={f.logo_url || duffelLogo(f.airline_code)} alt={f.airline} className="h-8 w-auto object-contain"
            onError={(e) => { const avs = airlineLogo(f.airline_code); if (e.target.src !== avs) { e.target.src = avs; } else { e.target.style.display = "none"; } }} />
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 text-sm truncate">{f.airline}</p>
            <p className="text-xs text-slate-400">{f.cabin}</p>
          </div>
        </div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0B3C5D] text-white">{f.trip_type}</span>
      </div>

      <div className="mt-4 space-y-3">
        <DirectionSummary label={isReturn ? "Outbound" : "Flight"} legs={outbound} fallback={obFallback} />
        {isReturn && (
          <>
            <div className="border-t border-dashed border-slate-200" />
            <DirectionSummary label="Return" legs={returnLegs} fallback={rtFallback} />
          </>
        )}
      </div>

      {hasSegments && (
        <button data-testid={`flight-details-${f.id}`} onClick={() => setOpenDetails((o) => !o)}
          className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#328CC1] hover:text-[#0B3C5D]">
          <Info className="w-3.5 h-3.5" /> {openDetails ? "Hide" : "View"} flight details, stops & flight numbers
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDetails ? "rotate-180" : ""}`} />
        </button>
      )}
      {openDetails && hasSegments && (
        <div data-testid={`flight-details-panel-${f.id}`} className="mt-1 rounded-xl bg-slate-50 border border-slate-100 p-3">
          {outbound.length > 0 && <SegmentDetail legs={outbound} title={`Outbound · ${outbound[0].date || ""}`} />}
          {returnLegs.length > 0 && <SegmentDetail legs={returnLegs} title={`Return · ${returnLegs[0].date || ""}`} />}
          <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1"><Info className="w-3 h-3" /> Baggage allowance is set by the operating airline. Times are local.</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
        <div>
          <p className="text-[11px] text-slate-400">{isReturn ? "Return total from" : "From"}</p>
          <p className="font-display text-2xl font-bold text-[#0B3C5D]">{gbp(f.price)}</p>
        </div>
        <div className="flex items-center gap-2">
          <QuoteDialog flight={f}
            trigger={<Button variant="outline" data-testid={`quote-flight-${f.id}`} className="rounded-full border-slate-300 text-[#0B3C5D] font-semibold"><FileText className="w-4 h-4 mr-1" /> Quote</Button>} />
          <BookingDialog productType="flight" item={f} unitPrice={f.price} defaultPax={pax}
            trigger={<Button data-testid={`book-flight-${f.id}`} className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold">Book <ArrowRight className="w-4 h-4 ml-1" /></Button>} />
        </div>
      </div>
    </div>
  );
};
